import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

/**
 * Change-password dialog usable by any logged-in user. Wrap any trigger element:
 *   <ChangePasswordDialog>
 *     <button>Change password</button>
 *   </ChangePasswordDialog>
 *
 * If no `children` are passed, renders a small icon button by default.
 */
export default function ChangePasswordDialog({ children }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        {children || (
          <button className="text-[color:var(--muted)] hover:text-[color:var(--ink)] inline-flex items-center gap-1.5 text-sm" data-testid="open-change-password">
            <KeyRound className="w-4 h-4" /> Change password
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Change password</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">Update the password for your account.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Current password" value={current} onChange={setCurrent} testid="cp-current" />
          <Field label="New password" value={next} onChange={setNext} testid="cp-new" />
          <Field label="Confirm new password" value={confirm} onChange={setConfirm} testid="cp-confirm" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="cp-submit">
              {loading ? "Saving…" : "Update"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{label}</label>
      <Input type="password" value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5" data-testid={testid} required />
    </div>
  );
}
